import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Target, Award } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CareerCoach = () => {
  const [insights, setInsights] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${sessionToken}` };

      const [insightsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/career-insights`, { headers }),
        axios.get(`${API}/performance-reviews`, { headers })
      ]);

      setInsights(insightsRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load career insights');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(pdf|docx|txt)$/i)) {
      toast.error('Please upload a PDF, DOCX, or TXT file');
      return;
    }

    setUploading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/performance-reviews/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Review uploaded and analyzed successfully!');
      fetchData();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload review');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-navy-900 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading insights...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              AI Career Coach
            </h1>
            <p className="text-gray-600">
              {insights?.total_reviews_analyzed || 0} performance reviews analyzed
            </p>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300 mb-8 text-center">
          <Upload className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--navy-600)' }} />
          <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
            Upload Performance Review
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Upload past performance reviews to get personalized career insights
          </p>
          <input
            type="file"
            id="review-upload"
            accept=".pdf,.docx,.txt"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <Button
            onClick={() => document.getElementById('review-upload').click()}
            disabled={uploading}
            className="px-6 py-3 rounded-lg font-medium transition-colors duration-200"
            style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
          >
            {uploading ? 'Uploading...' : 'Choose File'}
          </Button>
          <p className="text-xs text-gray-500 mt-2">
            Your files are encrypted and private. Supported: PDF, DOCX, TXT
          </p>
        </div>

        {insights && insights.total_reviews_analyzed === 0 ? (
          <div className="bg-white p-12 rounded-lg border border-gray-200 text-center">
            <p className="text-gray-600">
              Upload your first performance review to see insights drawn from your real feedback
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Strengths */}
            {insights?.strengths && insights.strengths.length > 0 && (
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--cream-100)' }}>
                    <Award className="w-6 h-6" style={{ color: 'var(--navy-900)' }} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                      Your Proven Strengths
                    </h2>
                    <p className="text-sm text-gray-600">Based on {insights.total_reviews_analyzed} performance reviews</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {insights.strengths.map((strength, index) => {
                    const percentage = Math.min((strength.frequency / insights.total_reviews_analyzed) * 100, 100);
                    return (
                      <div key={index} className="p-4 rounded-lg" style={{ backgroundColor: 'var(--navy-50)' }}>
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-semibold" style={{ color: 'var(--navy-900)' }}>
                            {index + 1}. {strength.theme}
                          </h3>
                          <span className="text-sm font-medium" style={{ color: 'var(--navy-600)' }}>
                            {Math.round(percentage)}%
                          </span>
                        </div>
                        <Progress value={percentage} className="mb-2" />
                        {strength.description && (
                          <p className="text-sm text-gray-600 italic">"{strength.description}"</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Mentioned in {strength.frequency} of {insights.total_reviews_analyzed} reviews
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Areas to Develop */}
            {insights?.improvements && insights.improvements.length > 0 && (
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--cream-100)' }}>
                    <Target className="w-6 h-6" style={{ color: 'var(--navy-900)' }} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                      Areas to Develop
                    </h2>
                    <p className="text-sm text-gray-600">Growth opportunities identified</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {insights.improvements.map((improvement, index) => (
                    <div key={index} className="p-4 rounded-lg border-l-4" style={{ borderColor: 'var(--cream-600)', backgroundColor: 'var(--cream-50)' }}>
                      <h3 className="font-semibold mb-1" style={{ color: 'var(--navy-900)' }}>
                        {index + 1}. {improvement.theme}
                      </h3>
                      {improvement.description && (
                        <p className="text-sm text-gray-700 mb-2">
                          Feedback: "{improvement.description}"
                        </p>
                      )}
                      <p className="text-xs text-gray-600">
                        Mentioned in {improvement.frequency} of {insights.total_reviews_analyzed} reviews
                      </p>
                      <div className="mt-3 p-3 rounded" style={{ backgroundColor: 'white' }}>
                        <p className="text-sm font-medium" style={{ color: 'var(--navy-900)' }}>
                          Tip: log wins related to {improvement.theme.toLowerCase()} to demonstrate growth
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Uploaded Reviews */}
            {reviews.length > 0 && (
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                  Uploaded Reviews
                </h2>
                <div className="space-y-3">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="p-4 rounded-lg border border-gray-200 hover:border-navy-200 transition-colors duration-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium" style={{ color: 'var(--navy-900)' }}>
                            {review.file_name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {review.review_period}
                            {review.overall_rating && ` • ${review.overall_rating}`}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500" style={{ fontFamily: 'JetBrains Mono' }}>
                          {new Date(review.upload_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default CareerCoach;